/**
 * Pure mapping + validation for the team member import (see TeamImport.tsx).
 * Kept free of React/DOM so it can be exercised headlessly.
 *
 * Role scoping is deliberately minimal for MVP: owner is never assigned by
 * import (owner = Louis, set by hand), and functional labels on the sheet
 * (Intern / Contractor / Freelance / Sales) become platform role `member`
 * with the label preserved as the person's title.
 */

import type { ParsedTable } from '../client/intake/types'

export type FieldKey = 'name' | 'email' | 'title' | 'role' | 'disciplines' | 'capacityHrsWeek' | 'inviteStatus'

export const FIELDS: { key: FieldKey; label: string; required?: boolean; aliases: string[] }[] = [
  { key: 'name', label: 'Name', required: true, aliases: ['name', 'fullname', 'employee', 'member', 'person', 'teammember'] },
  { key: 'email', label: 'Email', required: true, aliases: ['email', 'mail', 'emailaddress', 'workemail'] },
  { key: 'title', label: 'Title', aliases: ['title', 'jobtitle', 'position', 'job'] },
  { key: 'role', label: 'Platform role', aliases: ['role', 'platformrole', 'access', 'permission', 'accountrole'] },
  { key: 'disciplines', label: 'Disciplines', aliases: ['disciplines', 'discipline', 'team', 'department', 'skills', 'teams'] },
  { key: 'capacityHrsWeek', label: 'Capacity (hrs/wk)', aliases: ['capacity', 'capacityhrs', 'capacityhrsweek', 'hoursperweek', 'hrsweek', 'hours', 'hrs'] },
  { key: 'inviteStatus', label: 'Login status', aliases: ['status', 'invitestatus', 'invitesent', 'loginstatus', 'invite', 'login'] },
]

/**
 * Synonyms people type on rosters, keyed by the discipline they suggest. A
 * synonym only resolves when the tenant actually configured that discipline —
 * folding "delivery" into "ops" because ops was hardcoded is what made a
 * tenant-added discipline unusable in the first place.
 */
const DISCIPLINE_ALIASES: Record<string, string[]> = {
  sales: ['bd', 'biz dev', 'business development', 'new business'],
  marketing: ['mktg', 'growth', 'demand gen'],
  ops: ['operations', 'admin', 'back office'],
  delivery: ['fulfilment', 'fulfillment', 'production'],
  engineering: ['eng', 'dev', 'development'],
  design: ['creative'],
}

const RANDOM_COLORS = [
  '#00D1FF', '#3FB68B', '#E0A93B', '#E5484D', '#A855F7',
  '#F97316', '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6',
]

export function randomColor(): string {
  return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)] ?? '#00D1FF'
}

export function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  // Single-word names (common on informal rosters) take two letters, not one —
  // initials are a unique DB column, so 1-letter initials collide immediately.
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return parts
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
}

const norm = (h: string) => h.toLowerCase().replace(/[\s_\-./]+/g, '').replace(/[^a-z0-9]/g, '')

export function autoMap(headers: string[]): Record<number, FieldKey | null> {
  const map: Record<number, FieldKey | null> = {}
  const used = new Set<FieldKey>()
  headers.forEach((h, i) => {
    const n = norm(h)
    map[i] = null
    if (!n) return
    for (const f of FIELDS) {
      if (used.has(f.key)) continue
      if (f.aliases.some((a) => n === a || n.includes(a) || a.includes(n))) {
        map[i] = f.key
        used.add(f.key)
        break
      }
    }
  })
  return map
}

/**
 * Coerce a role cell. Functional labels (intern/contractor/freelance/sales)
 * map to platform role `member` and surface the label as a title. `owner` is
 * intentionally NOT accepted — the owner is assigned by hand, never by import.
 */
export function coerceRole(v: string): { role: string; title?: string } | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (['member', 'user', 'staff', 'employee'].includes(s)) return { role: 'member' }
  if (['admin', 'administrator', 'manager'].includes(s)) return { role: 'admin' }
  if (['readonly', 'read-only', 'read only', 'read_only', 'viewer'].includes(s)) return { role: 'read_only' }
  if (['intern'].includes(s)) return { role: 'member', title: 'Intern' }
  if (['contractor'].includes(s)) return { role: 'member', title: 'Contractor' }
  if (['freelance', 'freelancer'].includes(s)) return { role: 'member', title: 'Freelance' }
  if (['sales'].includes(s)) return { role: 'member', title: 'Sales' }
  return undefined
}

export function coerceInviteStatus(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (['roster_only', 'roster only', 'roster', 'rosteronly', 'no login'].includes(s)) return 'roster_only'
  if (['pending', 'invite', 'invited', 'invite now'].includes(s)) return 'pending'
  if (['active', 'live'].includes(s)) return 'active'
  // "Invite Sent" style Y/N columns: Y = invite already went out → pending;
  // N = nobody has been invited → roster only. Import never sends emails.
  if (['y', 'yes', 'sent', 'true'].includes(s)) return 'pending'
  if (['n', 'no', 'false', 'not sent', 'not yet'].includes(s)) return 'roster_only'
  return undefined
}

/** Map a roster cell onto the tenant's configured disciplines (exact match wins). */
export function coerceDisciplines(v: string, errors: string[], configured: string[]): string[] {
  const allowed = configured.map((d) => d.toLowerCase().trim())
  const tokens = v.split(/[,;/|]+/).map((t) => t.toLowerCase().trim()).filter(Boolean)
  const out: string[] = []
  for (const t of tokens) {
    const d = allowed.includes(t)
      ? t
      : allowed.find((target) => DISCIPLINE_ALIASES[target]?.includes(t))
    if (d) {
      if (!out.includes(d)) out.push(d)
    } else {
      errors.push(`Discipline "${t}" is not one of: ${allowed.join(', ')}`)
    }
  }
  return out
}

export interface EmployeeDraft {
  name: string
  initials: string
  email: string
  avatarColor: string
  title?: string
  role?: string
  disciplines?: string[]
  capacityHrsWeek?: number
  inviteStatus?: string
}

export interface MappedMember {
  draft: EmployeeDraft
  errors: string[]
  sourceRow: number
}

export function mapRows(
  table: ParsedTable,
  colMap: Record<number, FieldKey | null>,
  disciplines: string[],
): MappedMember[] {
  const seenEmails = new Set<string>()
  return table.rows.map((cells, ri) => {
    const draft: EmployeeDraft = { name: '', initials: '', email: '', avatarColor: randomColor() }
    const errors: string[] = []
    let roleTitle: string | undefined
    cells.forEach((cell, ci) => {
      const key = colMap[ci]
      const val = (cell ?? '').trim()
      if (!key || !val) return
      switch (key) {
        case 'name':
          draft.name = val
          break
        case 'email':
          if (/^\S+@\S+\.\S+$/.test(val)) draft.email = val.toLowerCase()
          else errors.push(`"${val}" is not a valid email`)
          break
        case 'title':
          draft.title = val
          break
        case 'role': {
          const r = coerceRole(val)
          if (r) {
            draft.role = r.role
            roleTitle = r.title
          } else {
            errors.push(`Role "${val}" not recognized (member, admin, read-only; intern/contractor/freelance/sales become member)`)
          }
          break
        }
        case 'disciplines':
          draft.disciplines = coerceDisciplines(val, errors, disciplines)
          break
        case 'capacityHrsWeek': {
          const n = Number(val.replace(/[^\d.]/g, ''))
          if (Number.isFinite(n) && n > 0) draft.capacityHrsWeek = Math.round(n)
          else errors.push(`Capacity "${val}" is not a number of hours`)
          break
        }
        case 'inviteStatus': {
          const s = coerceInviteStatus(val)
          if (s) draft.inviteStatus = s
          else errors.push(`Status "${val}" not recognized (roster only, invite, active, Y/N)`)
          break
        }
      }
    })
    if (!draft.name) errors.push('Name is required')
    if (!draft.email && !errors.some((e) => e.includes('valid email'))) errors.push('Email is required')
    if (draft.email) {
      if (seenEmails.has(draft.email)) errors.push(`Duplicate email in this file: ${draft.email}`)
      else seenEmails.add(draft.email)
    }
    draft.initials = deriveInitials(draft.name)
    // A functional role label doubles as the title unless the sheet has one.
    if (!draft.title && roleTitle) draft.title = roleTitle
    // New imports default to roster-only — inviting is an explicit choice.
    if (!draft.inviteStatus) draft.inviteStatus = 'roster_only'
    return { draft, errors, sourceRow: ri + 2 }
  })
}
